import { Button, Col, Row } from "antd";
import {
    useDestroyConnectionSequenceStepMutation,
    useGetConnectionSequenceStepsQuery,
    useStoreConnectionSequenceStepMutation,
    useUpdateConnectionSequenceStepMutation,
} from "api/TenantSlug/ConnectionSequenceStep/hooks";
import { EditorDrawers } from "components/Drawers/EditorDrawers/EditorDrawers";
import { FormData, schema } from "components/Forms/ConnectionCreatorEditorFormSections/formSchema";
import { NestedGeneralFormProvider } from "components/UI/GeneralForm/NestedProvider/NestedGeneralFormProvider";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { RequestMethod, RequestPayloadType } from "types/enums";
import { FormId } from "types/models";
import { transformRequestConfig } from "utils/editorTransformRequestConfig";

import { ApiMethodPanel } from "./ApiMethodPanel";

import ConnectionSequenceStepData = App.Data.ConnectionSequenceStepData;
import StoreConnectionSequenceStepRequest = App.Http.Requests.ConnectionCreator.StoreConnectionSequenceStepRequest;
import FormStructureData = App.Data.FormStructureData;
import FormFieldData = App.Data.FormFieldData;

interface FormStructureDetailEditorFormProps {
    formStructure: FormStructureData;
}

export const FormStructureDetailEditorForm: FC<FormStructureDetailEditorFormProps> = ({ formStructure }) => {
    const queryString = `meanings,
    requestConfig.responseHandlers.responseConnectionPropertiesCheck,
    requestConfig.requestConnectionPropertiesUrl,
    requestConfig.requestConnectionPropertiesUrl.requestConnectionPropertyModify,
    requestConfig.requestConnectionProperties,
    requestConfig.requestConnectionProperties.requestConnectionPropertyModify,
    requestConfig.headers,
    requestConfig.headers.requestConnectionPropertyModify,
    requestConfig.responseHandlers.stepConnection,
    requestConfig.responseHandlers.responseConnectionProperties,
    requestConfig.responseHandlers.responseConnectionProperties.requestConnectionPropertyModify`;

    const getConnectionSequenceStepsQuery = useGetConnectionSequenceStepsQuery({
        filter: {
            include: queryString.replace(/\s+/g, ""),
            filter: { form_structure_id: formStructure.id },
        },
    });

    const [deletedIds, setDeletedIds] = useState<Array<number | string>>([]);
    const [lastTempId, setLastTempId] = useState(0);

    const destroyConnectionSequenceStepMutation = useDestroyConnectionSequenceStepMutation();
    const storeConnectionSequenceStepMutation = useStoreConnectionSequenceStepMutation();
    const updateConnectionSequenceStepMutation = useUpdateConnectionSequenceStepMutation();

    const { t } = usePaldockTranslation("ui.connection-creator-editor");

    const emptyConnectionSequenceStepData = {
        id: null,
        tempId: null,
        name: "",
        position_x: 0,
        position_y: 0,
        model_type: "App\\Models\\FormStructure",
        model_id: formStructure.id || "",
        connection_sequence_step_next_id: null,
        connection_sequence_step_verify_phone_id: null,
        meanings: [],
        requestConfig: {
            id: "",
            name: "",
            method: RequestMethod.Get,
            url: "",
            timeout_ms: 0,
            send_payload_as: RequestPayloadType.Json,
            empty_fields_behavior: null,
            created_at: null,
            updated_at: null,
            offerConnectionSequenceSteps: [],
            requestConnectionProperties: [],
            responseHandlers: [],
            requestConnectionPropertiesUrl: [],
            headers: [],
        },
        type: null,
        wait_ms: null,
        created_at: null,
        updated_at: null,
    };

    if (!getConnectionSequenceStepsQuery.data?.connectionSequenceSteps) {
        return null;
    }

    const defaultValues: FormData = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        methods: getConnectionSequenceStepsQuery.data.connectionSequenceSteps.map((step) => ({
            ...step,
            tempId: step.id,
        })),
    };

    const onSubmit = async (values: FormData) => {
        if (!values.methods) {
            return;
        }

        const updatedValues = [...values.methods];

        const mutationPromises = updatedValues.map((method) => {
            const currentId = method.tempId;

            // TODO: rewrite for uuid; now type of if is used;
            // string - id from be
            // number - temporary id on fe

            if (deletedIds.includes(currentId)) {
                if (typeof currentId === "string") {
                    return destroyConnectionSequenceStepMutation.mutateAsync(currentId);
                } else {
                    return Promise.resolve();
                }
            } else {
                if (typeof currentId === "string") {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const updateMethod: StoreConnectionSequenceStepRequest = {
                        ...method,
                        model_type: "App\\Models\\FormStructure",
                        model_id: formStructure.id || "",
                        meanings: method?.meanings?.map((meaning) => {
                            return typeof meaning === "string"
                                ? { id: null, connection_sequence_step_id: null, type: meaning }
                                : meaning;
                        }),
                        requestConfig: transformRequestConfig(method.requestConfig as App.Data.RequestConfigData),
                    };

                    return updateConnectionSequenceStepMutation.mutateAsync(updateMethod);
                } else {
                    const storeMethod: StoreConnectionSequenceStepRequest = {
                        ...method,
                        model_type: "App\\Models\\FormStructure",
                        model_id: formStructure.id || "",
                        type: "send-request",
                        meanings: method?.meanings?.map((meaning) => {
                            return typeof meaning === "string"
                                ? { id: null, connection_sequence_step_id: null, type: meaning }
                                : meaning;
                        }),
                        requestConfig: transformRequestConfig(method.requestConfig as App.Data.RequestConfigData),
                    };

                    return storeConnectionSequenceStepMutation.mutateAsync(storeMethod);
                }
            }
        });

        try {
            for (const promise of mutationPromises) {
                await promise;
            }

            const res = await getConnectionSequenceStepsQuery.refetch();
            const newMethods = res.data?.connectionSequenceSteps as ConnectionSequenceStepData[];

            setDeletedIds([]);
            setLastTempId(0);

            toast.success(t("messages.methods_saved"));

            return {
                methods: newMethods.map((step) => ({
                    ...step,
                    tempId: step.id,
                })),
            };
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    return (
        <NestedGeneralFormProvider<FormData>
            key={formStructure.id}
            schema={schema}
            defaultValues={defaultValues}
            id={FormId.FormStructureEditor}
            onSubmit={onSubmit}
        >
            <FormContent
                formStructure={formStructure}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                emptyConnectionSequenceStepData={emptyConnectionSequenceStepData}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
                lastTempId={lastTempId}
                setLastTempId={setLastTempId}
            />
        </NestedGeneralFormProvider>
    );
};

interface FormContentProps {
    formStructure: FormStructureData;
    emptyConnectionSequenceStepData: ConnectionSequenceStepData;
    deletedIds: Array<number | string>;
    setDeletedIds: React.Dispatch<React.SetStateAction<Array<number | string>>>;
    lastTempId: number;
    setLastTempId: React.Dispatch<React.SetStateAction<number>>;
}

export const FormContent: FC<FormContentProps> = ({
    formStructure,
    emptyConnectionSequenceStepData,
    deletedIds,
    setDeletedIds,
    lastTempId,
    setLastTempId,
}) => {
    const { control } = useFormContext<FormData>();
    const { fields, append } = useFieldArray({ control, name: "methods" });
    const { t } = usePaldockTranslation("ui.connection-creator-editor");

    // const modifyIntegrationMethodsDrawerId = DrawerId.ModifyIntegrationMethods;

    const handleAddMethod = () => {
        setLastTempId((prevState) => prevState + 1);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        append({ ...emptyConnectionSequenceStepData, tempId: lastTempId });
    };

    const handleRemoveMethod = (tempId: string | number) => {
        setDeletedIds((prev) => [...prev, tempId]);
    };

    return (
        <div>
            {/* <Flex justify="space-between" className="mb-5">
                <Typography.Title level={5}>Metody</Typography.Title>
                <DrawerLink
                    drawerId={modifyIntegrationMethodsDrawerId}
                    action="show"
                    search={{
                        [`${modifyIntegrationMethodsDrawerId}-id`]: formStructure.id ?? "",
                    }}
                >
                    <Button type="primary">{"</>"}</Button>
                </DrawerLink>
            </Flex> */}

            {fields.map((field, index) => {
                const disabled = deletedIds.includes(field.tempId);
                return (
                    <ApiMethodPanel
                        key={field.id}
                        name={index}
                        disabled={disabled}
                        handleRemoveMethod={() => handleRemoveMethod(field.tempId)}
                        formStructure={formStructure}
                    />
                );
            })}

            <Row className="mt-3">
                <Col span={8} xs={4}>
                    <Button type="primary" onClick={handleAddMethod}>
                        {t("actions.add_new_method")}
                    </Button>
                </Col>
            </Row>
            <EditorDrawers leadFields={formStructure?.formFields as Array<FormFieldData>} />
        </div>
    );
};
